import { consumptionRateAt } from "@/features/voyagePlanner/voyageSettingsFields"

// ── Adapters: astar postprocessed GeoJSON FeatureCollection ──────────────────

function _getAstarPoints(fc, originalPointsOnly) {
  const points = (fc.features || []).filter(
    (f) => f.geometry?.type === "Point" && f.properties?.waypoint_number != null,
  )
  points.sort((a, b) => {
    const wna = a.properties.waypoint_number
    const wnb = b.properties.waypoint_number
    if (wna !== wnb) return wna - wnb
    return (a.properties.waypoint_subnumber ?? 0) - (b.properties.waypoint_subnumber ?? 0)
  })
  return originalPointsOnly
    ? points.filter((f) => f.properties.waypoint_type === "Regular")
    : points
}

// astar timestamps come as either RFC-3339 with offset ("…T08:13:00+00:00")
// or naive UTC ("2026-04-02 12:25:22"). Return unix seconds, or null.
function _parseUtcSeconds(dtStr) {
  if (!dtStr) return null
  // Naive form (space, no timezone) → treat as UTC by appending Z.
  const normalized = /[T+Z]|[+-]\d{2}:\d{2}$/.test(dtStr)
    ? dtStr
    : dtStr.replace(" ", "T") + "Z"
  const ms = new Date(normalized).getTime()
  return Number.isFinite(ms) ? ms / 1000 : null
}

function _astarPointsToWps(astarPoints) {
  return astarPoints.map((f) => {
    const props = f.properties || {}
    const [lon, lat] = f.geometry?.coordinates || [0, 0]
    const dtStr = props.datetime_utc
    return {
      // Preserve full pathfinder response properties so downstream persistence
      // (savePlannedRoute → extractPathfinderFields) can read weather, leg_data,
      // ww_*, warnings, sum_*, etc. Explicit keys below override where needed.
      ...props,
      lon,
      lat,
      // astar emits either RFC-3339 ("2026-07-14T08:13:00+00:00") or naive UTC
      // ("2026-04-02 12:25:22"); parse both → unix seconds.
      timestamp: _parseUtcSeconds(dtStr),
      u10: props.wind_gfs_u10_interp ?? null,
      v10: props.wind_gfs_v10_interp ?? null,
      dmean: props.wave_nww3_mean_direction_deg_interp ?? null,
      hssw: props.wave_nww3_significant_height_m_interp ?? null,
      ucur: props.current_hycom_u_interp ?? null,
      vcur: props.current_hycom_v_interp ?? null,
      type: props.waypoint_type === "Regular" ? "original_point" : "weather_point",
      legType: 1,
      node_id: props.node_id ?? null,
    }
  })
}

// Normalise the astar postprocessed FeatureCollection to a flat wps array
function _extractWps(data, originalPointsOnly) {
  return _astarPointsToWps(_getAstarPoints(data, originalPointsOnly))
}

// ────────────────────────────────────────────────────────────────────────────

/**
 * Build an astar-like FeatureCollection from planned route data fetched via
 * the core CRUD APIs (/waypoints, /speed-profiles, /consumption-profiles).
 *
 * @param {Array}  rawWaypoints       - Waypoint objects from GET /waypoints
 * @param {Array}  speedProfiles      - Request-ready matrices from /speed-profiles ([wind, wave])
 * @param {*}      consumptionProfile - Request-ready profile from /consumption-profiles, or null
 * @param {Object} [options]          - { jsonRequest, summary } from the stored route row;
 *                                      merged into the json_request / route_summary features
 */
function plannedWaypointsToFC(rawWaypoints, speedProfiles, consumptionProfile, options = {}) {
  const sorted = [...(rawWaypoints ?? [])].sort((a, b) => {
    const an = Number(a?.number ?? 0)
    const bn = Number(b?.number ?? 0)
    if (an !== bn) return an - bn
    return (
      Number(a?.extendedNumber ?? a?.extended_number ?? 0) -
      Number(b?.extendedNumber ?? b?.extended_number ?? 0)
    )
  })

  const toDatetimeUtc = (ts) => {
    if (!ts) return null
    try { return new Date(ts).toISOString().slice(0, 19) } catch { return null }
  }

  const firstWithSpeed = rawWaypoints?.find(
    (wp) => wp?.basespeed != null && Number.isFinite(Number(wp.basespeed)),
  )
  const basespeedMsec = Number(firstWithSpeed?.basespeed) || 0

  const toProfileProps = (p) =>
    p
      ? {
          speedprofile_rows_angles: p.speedprofile_rows_angles,
          speedprofile_columns_strengths: p.speedprofile_columns_strengths,
          speedprofile_percent_speeds: p.speedprofile_percent_speeds,
        }
      : {}

  const waypointFeatures = sorted.map((wp, i) => {
    const [lon, lat] = wp?.lonlat ?? [0, 0]
    return {
      type: "Feature",
      geometry: { type: "Point", coordinates: [Number(lon), Number(lat)] },
      properties: {
        ...(wp?.weatherSnapshot ?? {}),
        waypoint_number: i,
        waypoint_subnumber: 0,
        waypoint_type: wp?.type === "original_point" ? "Regular" : "Weather",
        datetime_utc: toDatetimeUtc(wp?.timestamp),
        node_id: wp?.node_id ?? null,
        ...(wp?.legtype != null ? { legtype: wp.legtype } : {}),
        ...(wp?.sog != null && Number.isFinite(Number(wp.sog))
          ? { sog: Number(wp.sog) }
          : {}),
      },
    }
  })

  // Without this, core's voyageFile export would fall back to
  // json_request.profiles when the dedicated profiles feature is empty.
  const { profiles: _requestProfiles, ...jsonRequestRest } = options?.jsonRequest ?? {}

  const summaryFeature =
    options?.summary && typeof options.summary === "object"
      ? [
          {
            type: "Feature",
            geometry: null,
            properties: { ...options.summary, type: "route_summary" },
          },
        ]
      : []

  return {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        geometry: null,
        properties: {
          // Original astar request (wind_layer/wave_layer limits etc. for the
          // ECDIS exports); departure and vessel keep the DB-derived values.
          ...jsonRequestRest,
          type: "json_request",
          departure: { datetime_utc: toDatetimeUtc(sorted[0]?.timestamp) },
          vessel: { basespeed_msec: basespeedMsec },
        },
      },
      ...summaryFeature,
      {
        type: "Feature",
        geometry: null,
        properties: {
          type: "profiles",
          wind: toProfileProps(speedProfiles?.[0]),
          wave: toProfileProps(speedProfiles?.[1]),
          ...(consumptionProfile != null ? { consumption: consumptionProfile } : {}),
        },
      },
      {
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates: sorted.map((wp) => {
            const [lon, lat] = wp?.lonlat ?? [0, 0]
            return [Number(lon), Number(lat)]
          }),
        },
        properties: { type: "path_linestring" },
      },
      ...waypointFeatures,
    ],
  }
}

function computeTotalConsumption(data, baseSpeed, consumptionProfileData) {
  const waypoints = _extractWps(data, false)

  // Total time from the first/last valid unix timestamps (in seconds).
  const firstTimestamp = waypoints[0]?.timestamp
  const lastTimestamp = waypoints[waypoints.length - 1]?.timestamp
  if (!Number.isFinite(firstTimestamp) || !Number.isFinite(lastTimestamp)) {
    return null
  }
  const totalTimeDays = (lastTimestamp - firstTimestamp) / 3600 / 24

  // Consumption rate (tons/day) at baseSpeed, interpolated over the profile.
  const consumptionRate =
    consumptionRateAt(baseSpeed, consumptionProfileData) ?? 0

  // Total consumption (tons) = consumption rate (tons/day) * total time (days)
  return consumptionRate * totalTimeDays
}

// Filter out reported-route line segments where wp1 or wp2 has albis_reports
// and reconnect neighbouring non-ALBIS segments with a bridge segment.
function filterAlbisSegmentsWithBridge(routeLine) {
  const rawLineGeojson = routeLine.geojson
  if (!rawLineGeojson?.features || routeLine.routeType !== "reported") {
    return rawLineGeojson
  }

  const feats = rawLineGeojson.features

  const isAlbisSeg = (f) => {
    const wp1Props = f?.properties?.analyse?.wp1?.properties
    const wp2Props = f?.properties?.analyse?.wp2?.properties

    const wp1HasAlbis = wp1Props && wp1Props.albis_reports != null
    const wp2HasAlbis = wp2Props && wp2Props.albis_reports != null

    return wp1HasAlbis || wp2HasAlbis
  }

  const newFeatures = []
  const n = feats.length
  let i = 0

  while (i < n) {
    const f = feats[i]

    // normales Segment ohne ALBIS → übernehmen
    if (!isAlbisSeg(f)) {
      newFeatures.push(f)
      i++
      continue
    }

    // Block von ALBIS-Segmenten sammeln
    const startIdx = i
    while (i < n && isAlbisSeg(feats[i])) {
      i++
    }
    const endIdx = i - 1 // aktuell ungenutzt, aber ggf. später hilfreich

    const prevNonAlbis = startIdx - 1 >= 0 ? feats[startIdx - 1] : null
    const nextNonAlbis = i < n ? feats[i] : null

    // Wenn auf beiden Seiten normale Segmente existieren → Brücke bauen
    if (prevNonAlbis && nextNonAlbis) {
      const prevEnd = prevNonAlbis.geometry?.coordinates?.[1]
      const nextStart = nextNonAlbis.geometry?.coordinates?.[0]

      if (prevEnd && nextStart) {
        const template = feats[startIdx]
        const wp1Base = template?.properties?.analyse?.wp1 || {}
        const wp2Base = template?.properties?.analyse?.wp2 || {}

        const wp1Props = { ...(wp1Base.properties || {}) }
        const wp2Props = { ...(wp2Base.properties || {}) }
        delete wp1Props.albis_reports
        delete wp2Props.albis_reports

        const bridge = {
          ...template,
          geometry: {
            ...(template.geometry || {}),
            type: "LineString",
            coordinates: [prevEnd, nextStart],
          },
          properties: {
            ...(template.properties || {}),
            analyse: {
              ...(template.properties?.analyse || {}),
              wp1: {
                ...(wp1Base || {}),
                geometry: { type: "Point", coordinates: prevEnd },
                properties: wp1Props,
                type: "Feature",
              },
              wp2: {
                ...(wp2Base || {}),
                geometry: { type: "Point", coordinates: nextStart },
                properties: wp2Props,
                type: "Feature",
              },
            },
          },
        }

        newFeatures.push(bridge)
      }
    }
    // Wenn kein prev oder kein next existiert, wird der ALBIS-Block einfach entfernt
  }

  return {
    ...rawLineGeojson,
    features: newFeatures,
  }
}

// Filter out reported-route points where feature.properties.albis_reports is set
function filterAlbisPoints(routePoints) {
  const rawPointsGeojson = routePoints.geojson
  if (!rawPointsGeojson?.features || routePoints.routeType !== "reported") {
    return rawPointsGeojson
  }

  const filteredFeatures = rawPointsGeojson.features.filter((f) => {
    const props = f?.properties
    // Drop point if it has an albis_reports object
    return !(props && props.albis_reports != null)
  })

  return {
    ...rawPointsGeojson,
    features: filteredFeatures,
  }
}

// Convenience wrapper: filter ALBIS from both points and lines of a route object
function filterAlbisFromRoute(route) {
  if (!route) return route

  // Filter lines (ALBIS-Segmente + Brücken)
  const filteredLinesGeojson = route.lines ? filterAlbisSegmentsWithBridge(route.lines) : route.lines?.geojson

  // Filter points (ALBIS-Report-Punkte entfernen)
  const filteredPointsGeojson = route.points ? filterAlbisPoints(route.points) : route.points?.geojson

  return {
    ...route,
    points: route.points
      ? {
          ...route.points,
          geojson: filteredPointsGeojson,
        }
      : route.points,
    lines: route.lines
      ? {
          ...route.lines,
          geojson: filteredLinesGeojson,
        }
      : route.lines,
  }
}

// Public helper — normalises either format to a flat wps array
function extractWaypoints(data, { originalPointsOnly = false } = {}) {
  return _extractWps(data, originalPointsOnly)
}

export {
  _parseUtcSeconds as parseUtcSeconds,
  extractWaypoints,
  plannedWaypointsToFC,
  computeTotalConsumption,
  filterAlbisSegmentsWithBridge,
  filterAlbisPoints,
  filterAlbisFromRoute,
}
